import React from 'react';
import { Document, Page, Text, View, StyleSheet, Font, Image } from '@react-pdf/renderer';
import DejaVuSans from '../fonts/dejavu-sans.book.ttf';
import DejaVuSansBold from '../fonts/dejavu-sans.bold.ttf';
import corevistaLogo from '../assets/corevista_logo.png';

// Register fonts
Font.register({
    family: 'dejavu-sans',
    fonts: [
        { src: DejaVuSans, fontWeight: 'normal' },
        { src: DejaVuSansBold, fontWeight: 'bold' }
    ]
});

const CEDI_SYMBOL = '₵';
const formatCurrency = (amount) => `${CEDI_SYMBOL} ${Number(amount || 0).toFixed(2)}`;
const cleanCurrency = (amount) => `${Number(amount || 0).toFixed(2)}`;

const styles = StyleSheet.create({
    page: { padding: 40, fontFamily: 'dejavu-sans', fontSize: 10, color: '#333' },
    headerContainer: { marginBottom: 30 },
    headerRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 },
    headerLogo: { width: 240, height: 90, objectFit: 'contain', marginRight: -75 },
    title: { fontSize: 24, fontWeight: 'bold', color: '#1a237e', textAlign: 'left', textTransform: 'uppercase' },

    companySection: { marginBottom: 0 },
    companyName: { fontSize: 13, fontWeight: 'bold', color: '#1a237e', marginBottom: 4 },
    textLine: { fontSize: 10, marginBottom: 4, color: '#444' },
    textLineRight: { fontSize: 10, marginBottom: 4, color: '#444', textAlign: 'right' },

    invoiceMetaSection: { alignItems: 'flex-end' },

    billToSection: { marginBottom: 30 },
    billToTitle: { fontSize: 12, fontWeight: 'bold', marginBottom: 8, color: '#1a237e' },

    tableSection: { marginBottom: 20 },
    tableHeaderRow: { flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: '#333', paddingBottom: 6, marginBottom: 8 },
    tableRow: { flexDirection: 'row', paddingVertical: 6, borderBottomWidth: 1, borderBottomColor: '#eee' },
    colItem: { width: '10%' },
    colDesc: { flex: 1 },
    colPeriod: { width: '20%' },
    colAmount: { width: '20%', textAlign: 'right' },
    boldText: { fontWeight: 'bold' },

    totalsSection: { alignItems: 'flex-end', marginBottom: 40 },
    totalLine: { flexDirection: 'row', justifyContent: 'space-between', width: 220, marginBottom: 6 },
    totalLineDu: { flexDirection: 'row', justifyContent: 'space-between', width: 220, marginTop: 6, paddingTop: 6, borderTopWidth: 1, borderTopColor: '#333' },

    paymentDetails: { marginTop: 20, borderTopWidth: 1, borderTopColor: '#eee', paddingTop: 15 },
    paymentTitle: { fontSize: 12, fontWeight: 'bold', marginBottom: 8, color: '#1a237e' },

    statusBadge: { paddingHorizontal: 12, paddingVertical: 6, borderRadius: 4, borderWidth: 1.5, alignSelf: 'flex-end', marginBottom: 15 },
    statusPaid: { borderColor: '#4caf50', backgroundColor: '#e8f5e9' },
    statusPending: { borderColor: '#ff9800', backgroundColor: '#fff3e0' },
    statusTextPaid: { fontSize: 11, fontWeight: 'bold', color: '#2e7d32', textTransform: 'uppercase', letterSpacing: 1 },
    statusTextPending: { fontSize: 11, fontWeight: 'bold', color: '#ef6c00', textTransform: 'uppercase', letterSpacing: 1 },

    watermark: { position: 'absolute', top: 300, left: 100, fontSize: 80, color: 'rgba(26, 35, 126, 0.03)', transform: 'rotate(-30deg)', fontWeight: 'bold' },
});

const TransactionPDF = ({ transaction, school, schoolInfoProfile }) => {
    const isInvoice = transaction.type === 'invoice';
    const docTitle = isInvoice ? 'INVOICE' : 'RECEIPT';
    const txDate = new Date(transaction.date || transaction.createdAt);
    const dueDate = transaction.dueDate ? new Date(transaction.dueDate) : null;
    const isPaid = transaction.status === 'paid';

    const refNumber = isInvoice ? `INV-${transaction.reference || transaction._id.slice(-5).toUpperCase()}` : `REC-${transaction.reference || transaction._id.slice(-5).toUpperCase()}`;

    return (
        <Document>
            <Page size="A4" style={styles.page}>
                <View fixed style={styles.watermark}>
                    <Text>COREVISTA</Text>
                </View>

                <View style={styles.headerContainer}>
                    <View style={styles.headerRow}>
                        <Text style={styles.title}>{docTitle}</Text>
                        <Image src={corevistaLogo} style={styles.headerLogo} />
                    </View>

                    <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <View style={styles.companySection}>
                            <Text style={styles.companyName}>CoreVista Technologies</Text>
                            <Text style={styles.textLine}>Address: Accra, Ghana</Text>
                            <Text style={styles.textLine}>Phone: +233 5464 0478</Text>
                            <Text style={styles.textLine}>Email: contactcorevista@gmail.com</Text>
                        </View>

                        <View style={{ alignItems: 'flex-end' }}>
                            {/* Only show status badge on receipts, never invoices */}
                            {!isInvoice && (
                                <View style={[styles.statusBadge, isPaid ? styles.statusPaid : styles.statusPending]}>
                                    <Text style={isPaid ? styles.statusTextPaid : styles.statusTextPending}>
                                        {transaction.status.toUpperCase()}
                                    </Text>
                                </View>
                            )}

                            <View style={styles.invoiceMetaSection}>
                                <Text style={styles.textLineRight}>
                                    <Text style={styles.boldText}>{isInvoice ? 'Invoice Number:' : 'Receipt Number:'} </Text>
                                    {refNumber}
                                </Text>
                                <Text style={styles.textLineRight}>
                                    <Text style={styles.boldText}>Date Issued: </Text>
                                    {txDate.toLocaleDateString()}
                                </Text>
                                {isInvoice && dueDate && (
                                    <Text style={styles.textLineRight}>
                                        <Text style={styles.boldText}>Due Date: </Text>
                                        {dueDate.toLocaleDateString()}
                                    </Text>
                                )}
                            </View>
                        </View>
                    </View>
                </View>

                <View style={styles.billToSection}>
                    <Text style={styles.billToTitle}>Bill To:</Text>
                    <Text style={[styles.textLine, styles.boldText, { fontSize: 11 }]}>{school.name || 'School Name Not Provided'}</Text>
                    <Text style={styles.textLine}>Address: {schoolInfoProfile?.address || school.address || 'Address Not Provided'}</Text>
                    <Text style={styles.textLine}>Phone: {schoolInfoProfile?.phone || school.phone || 'Phone Not Provided'}</Text>
                    <Text style={styles.textLine}>Attention: Administrator</Text>
                </View>

                <View style={styles.tableSection}>
                    <Text style={[styles.billToTitle, { marginBottom: 12 }]}>Description of Services</Text>
                    <View style={styles.tableHeaderRow}>
                        <Text style={[styles.textLine, styles.boldText, styles.colItem]}>Item</Text>
                        <Text style={[styles.textLine, styles.boldText, styles.colDesc]}>Description</Text>
                        <Text style={[styles.textLine, styles.boldText, styles.colPeriod]}>Period</Text>
                        <Text style={[styles.textLine, styles.boldText, styles.colAmount]}>Amount ({CEDI_SYMBOL})</Text>
                    </View>

                    {/* Render each item as a standalone numbered row */}
                    {transaction.items && transaction.items.length > 0 ? (
                        transaction.items.map((item, index) => (
                            <View style={styles.tableRow} key={index}>
                                <Text style={[styles.textLine, styles.colItem]}>{index + 1}</Text>
                                <Text style={[styles.textLine, styles.colDesc]}>{item.description || 'Platform Services'}</Text>
                                <Text style={[styles.textLine, styles.colPeriod]}>Current</Text>
                                <Text style={[styles.textLine, styles.colAmount]}>{cleanCurrency(item.amount)}</Text>
                            </View>
                        ))
                    ) : (
                        /* Fallback for old transactions without specific item arrays */
                        <View style={styles.tableRow}>
                            <Text style={[styles.textLine, styles.colItem]}>1</Text>
                            <Text style={[styles.textLine, styles.colDesc]}>{transaction.description || 'Platform Services'}</Text>
                            <Text style={[styles.textLine, styles.colPeriod]}>Current</Text>
                            <Text style={[styles.textLine, styles.colAmount]}>{cleanCurrency(transaction.amount)}</Text>
                        </View>
                    )}
                </View>

                <View style={styles.totalsSection}>
                    <View style={styles.totalLine}>
                        <Text style={[styles.textLine, styles.boldText]}>Subtotal:</Text>
                        <Text style={styles.textLine}>{formatCurrency(transaction.amount)}</Text>
                    </View>
                    <View style={styles.totalLineDu}>
                        <Text style={[styles.textLine, styles.boldText, { fontSize: 11 }]}>Total Amount {isInvoice ? 'Due' : 'Paid'}:</Text>
                        <Text style={[styles.textLine, styles.boldText, { fontSize: 11 }]}>{formatCurrency(transaction.amount)}</Text>
                    </View>
                </View>

                <View style={styles.paymentDetails}>
                    <Text style={styles.paymentTitle}>Payment Details</Text>
                    <Text style={styles.textLine}>Account Name: Evans Osei</Text>
                    <Text style={styles.textLine}>Bank Name: Ecobank Ghana</Text>
                    <Text style={styles.textLine}>Account Number: 1441002739478</Text>
                </View>

            </Page>
        </Document>
    );
};

export default TransactionPDF;
